import { Trash2 } from 'lucide-react';
import { useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { FormField, FieldLabel, TextField, Textarea, RadioCardGroup } from '~/components/form';
import { RecordPageShell } from '~/components/layout';
import ModelSelect from '~/components/pages/ai/common/ModelSelect';
import PlacementIdentityFields from '~/components/pages/ai/placements/PlacementIdentityFields';
import ComponentPickerModal from '~/components/pages/ai/placements/ComponentPickerModal';
import PlacementHistoryTab from '~/components/pages/ai/placements/PlacementHistoryTab';
import PlacementTestWorkspace from '~/components/pages/ai/placements/PlacementTestWorkspace';
import TestDataModal, {
  type PlacementTestDataTab,
} from '~/components/pages/ai/placements/TestDataModal';
import {
  buildSnippetFromComponentVersion,
  resolveComponentVersionOption,
} from '~/components/pages/ai/placements/preview/registry';
import {
  Alert,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  CardSection,
  ConfirmModal,
  FormActions,
  Tabs,
  TagsEditor,
} from '~/components/ui';
import { useFormValidation } from '~/hooks/useFormValidation';
import { useRecordDraft } from '~/hooks/useRecordDraft';
import { useRecordSave } from '~/hooks/useRecordSave';
import { useTransientParam } from '~/hooks/useTransientParam';
import { useUnsavedGuard } from '~/hooks/useUnsavedGuard';
import { useUrlParams } from '~/hooks/useUrlParams';
import { apiErrorMessage, errorText } from '~/lib/apiError';
import {
  DEFAULT_PLACEMENT_SAMPLE_CONTEXT,
  DEFAULT_PLACEMENT_SAMPLE_PROPS,
  PLACEMENT_COMPONENTS,
  type PlacementDraftFormState,
} from '~/lib/ai/placementDefaults';
import {
  placementDraftSchema,
  readPlacementFieldErrors,
  validatePlacementDraftInput,
  type PlacementFieldErrors,
} from '~/lib/ai/placementValidation';
import {
  useDeletePlacementMutation,
  useGetPlacementQuery,
  useUpdatePlacementDraftMutation,
} from '~/modules/query/ai/placements';
import { useGetProvidersQuery } from '~/modules/query/ai/providers';
import type {
  PlacementConfigDetail,
  PlacementComponentVersionOption,
} from '~/modules/query/ai/types';
import { routes } from '~/app/routes';

const STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft', description: 'Saved but not used by the app yet.' },
  { value: 'active', label: 'Active', description: 'Live wherever this placement appears.' },
] as const;

type PlacementTab = 'config' | 'history';

const displayNameOf = (placement: PlacementConfigDetail): string =>
  placement.name?.trim() || placement.placementKey;

/** What the form edits. The test data (`sample*`) is not part of the record, so it lives apart from the draft. */
type PlacementEditorDraft = Omit<PlacementDraftFormState, 'sampleContext' | 'sampleProps'>;

const samplesForVersion = (version: PlacementComponentVersionOption | null) => ({
  sampleContext: version
    ? JSON.stringify(version.defaultContext, null, 2)
    : DEFAULT_PLACEMENT_SAMPLE_CONTEXT,
  sampleProps: version
    ? JSON.stringify(version.defaultProps, null, 2)
    : DEFAULT_PLACEMENT_SAMPLE_PROPS,
});

const formFromPlacement = (placement: PlacementConfigDetail): PlacementEditorDraft => ({
  placementKey: placement.placementKey,
  componentId: placement.componentId,
  componentVersion: placement.componentVersion,
  promptTemplate: placement.promptTemplate,
  model: placement.model,
  name: placement.name ?? '',
  description: placement.description ?? '',
  changeNote: placement.latestChangeNote ?? '',
  status: placement.status,
});

const FORM_ID = 'ai-placement-form';

/** The field a failed submit focuses. */
const FIELD_IDS: Record<string, string> = {
  name: 'ai-placement-name',
  description: 'ai-placement-description',
  componentId: 'ai-placement-component',
  componentVersion: 'ai-placement-component',
  model: 'ai-placement-model',
  promptTemplate: 'ai-placement-prompt',
  changeNote: 'ai-placement-change-note',
};

export default function PlacementEditorPage() {
  const { placementKey = '' } = useParams<{ placementKey: string }>();
  // Keyed, so moving to another placement starts with fresh test data and no stale errors.
  return <PlacementEditor key={placementKey} placementKey={placementKey} />;
}

function PlacementEditor({ placementKey }: { placementKey: string }) {
  // On-screen only, like upstream: tags are never sent to the service. The parent keys this editor by
  // placementKey, so they reset when the placement changes.
  const [tags, setTags] = useState<string[]>([]);
  const url = useUrlParams();
  const navigate = useNavigate();
  const {
    currentData: data,
    isLoading,
    error,
    refetch,
  } = useGetPlacementQuery(placementKey, {
    skip: !placementKey,
  });
  const providers = useGetProvidersQuery();
  const [updatePlacementDraft] = useUpdatePlacementDraftMutation();
  const [deletePlacement, { isLoading: isDeleting }] = useDeletePlacementMutation();

  const placement = data?.placement;
  const {
    draft: form,
    patch: updateForm,
    dirty,
    reset,
    markSaved,
  } = useRecordDraft(placement, {
    id: placementKey,
    version: placement?.updatedAt,
    recordId: (p) => p.placementKey,
    toDraft: formFromPlacement,
  });
  const guard = useUnsavedGuard(dirty);
  const check = useFormValidation(placementDraftSchema, form, (field) => FIELD_IDS[field] ?? field);

  const [serverErrors, setServerErrors] = useState<PlacementFieldErrors>({});
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [isTestDataOpen, setIsTestDataOpen] = useState(false);
  const [testDataTab, setTestDataTab] = useState<PlacementTestDataTab>('context');
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  // Test data an operator typed; until then it is the selected component version's defaults.
  const [sampleEdits, setSampleEdits] = useState<{ context?: string; props?: string }>({});
  const savedPlacement = useRef<PlacementConfigDetail | null>(null);

  const tab: PlacementTab = url.get('tab') === 'history' ? 'history' : 'config';
  const viewer = useTransientParam('version');
  const versionParam = viewer.value;
  const version = versionParam && /^\d+$/.test(versionParam) ? Number(versionParam) : null;

  const selectedVersion = form
    ? resolveComponentVersionOption(PLACEMENT_COMPONENTS, form.componentId, form.componentVersion)
    : null;
  const selectedComponent = form
    ? (PLACEMENT_COMPONENTS.find((component) => component.id === form.componentId) ?? null)
    : null;
  const defaultSamples = samplesForVersion(selectedVersion);
  const sampleContext = sampleEdits.context ?? defaultSamples.sampleContext;
  const sampleProps = sampleEdits.props ?? defaultSamples.sampleProps;
  const renderSnippet =
    form && selectedVersion
      ? buildSnippetFromComponentVersion(selectedVersion, form.placementKey)
      : '';
  const title = form?.name.trim() || (placement ? displayNameOf(placement) : placementKey);

  const save = useRecordSave<PlacementEditorDraft>({
    save: async (draft) => {
      const validation = validatePlacementDraftInput({
        placementKey: draft.placementKey,
        componentId: draft.componentId,
        componentVersion: draft.componentVersion,
        description: draft.description.trim() || undefined,
        promptTemplate: draft.promptTemplate,
        model: draft.model,
        name: draft.name.trim() || undefined,
        changeNote: draft.changeNote.trim(),
        status: draft.status,
      });
      if (!validation.success) throw new Error(validation.error.message);

      try {
        const response = await updatePlacementDraft({
          placementKey: validation.data.placementKey,
          updates: {
            componentId: validation.data.componentId,
            componentVersion: validation.data.componentVersion,
            description: validation.data.description,
            promptTemplate: validation.data.promptTemplate,
            model: validation.data.model,
            status: validation.data.status,
            name: validation.data.name,
            changeNote: validation.data.changeNote,
          },
        }).unwrap();
        savedPlacement.current = response.placement;
      } catch (mutationError) {
        setServerErrors(
          readPlacementFieldErrors((mutationError as { data?: unknown } | undefined)?.data),
        );
        throw mutationError;
      }
    },
    success: 'Placement saved',
    failure: 'The placement could not be saved. Try again.',
  });

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!form || !check.submit()) return;
    setServerErrors({});
    if ((await save.submit(form)) && savedPlacement.current) {
      markSaved(form, formFromPlacement(savedPlacement.current));
    }
  };

  const confirmDelete = async () => {
    if (!form || isDeleting) {
      return;
    }

    try {
      await deletePlacement(form.placementKey).unwrap();
      toast.success(`Placement ${title} deleted`);
      guard.release();
      navigate(routes.aiPlacements, { replace: true });
    } catch (mutationError) {
      toast.error(errorText(mutationError, 'The placement could not be deleted. Try again.'));
    }
  };

  const headerActions = form
    ? [
        {
          label: 'Delete placement',
          kind: 'danger' as const,
          testId: 'ai-placement-delete',
          startIcon: <Trash2 className="size-4" aria-hidden="true" />,
          disabled: save.busy,
          onClick: () => setIsDeleteOpen(true),
        },
      ]
    : [];

  const notFound = !placementKey || (!isLoading && !placement && !error);
  const loadFailed = Boolean(error) && !placement;

  return (
    <RecordPageShell
      title={notFound ? 'Placement' : title}
      breadcrumbs={[
        { label: 'AI' },
        { label: 'Placements', href: routes.aiPlacements },
        { label: notFound ? 'Not found' : title },
      ]}
      actions={headerActions}
      loading={!notFound && !loadFailed && !form}
      notFound={notFound}
      notFoundTitle="Placement not found"
      backTo={routes.aiPlacements}
      backLabel="Back to placements"
      error={
        loadFailed && !notFound ? { what: 'the placement', message: apiErrorMessage(error) } : null
      }
      onRetry={() => refetch()}
      guard={guard}
      testId="ai-placement"
    >
      {form && (
        <>
          <Tabs
            variant="underline"
            ariaLabel="Placement sections"
            idPrefix="placement"
            activeId={tab}
            onChange={(id) => url.set({ tab: id === 'config' ? null : id, version: null })}
            tabs={[
              { id: 'config', label: 'Config' },
              { id: 'history', label: 'Version history' },
            ]}
          />

          {tab === 'config' ? (
            <section
              role="tabpanel"
              id="placement-panel-config"
              aria-labelledby="placement-tab-config"
            >
              <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
                <div className="xl:col-span-2">
                  <form id={FORM_ID} noValidate onSubmit={submit}>
                    <Card>
                      <CardHeader
                        title="Configuration"
                        description="How this placement behaves wherever it appears."
                      />
                      <CardBody className="space-y-6">
                        {save.error && <Alert variant="error" message={save.error} />}
                        <CardSection
                          title="Details"
                          description="Name, identity and the model behind the prompt."
                        >
                          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                            <PlacementIdentityFields
                              isNew={false}
                              name={form.name}
                              placementKey={form.placementKey}
                              onNameChange={(name) => updateForm({ name })}
                              onNameBlur={() => check.touch('name')}
                              nameError={check.errors.name ?? serverErrors.name}
                            />

                            <div className="lg:col-span-2">
                              <TextField
                                id="ai-placement-description"
                                label="Description"
                                testId="ai-placement-description"
                                value={form.description}
                                onChange={(value) => updateForm({ description: value })}
                                onBlur={() => check.touch('description')}
                                error={check.errors.description ?? serverErrors.description}
                                placeholder="Where this placement appears and what the prompt is for"
                                helperText="Optional. One sentence the team can scan in the list."
                              />
                            </div>

                            <div className="lg:col-span-2">
                              <TagsEditor
                                tags={tags}
                                onChange={setTags}
                                label="Tags"
                                tooltip="Tags aren't saved yet. They clear when you leave this placement."
                                testId="ai-placement-tags"
                              />
                              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                                Tags aren't saved yet.
                              </p>
                            </div>

                            <div className="lg:col-span-2">
                              <FormField
                                id="ai-placement-component"
                                label="Component"
                                error={check.errors.componentId ?? serverErrors.componentId}
                                helperText={
                                  selectedComponent
                                    ? selectedComponent.description
                                    : 'Choose where this placement appears.'
                                }
                              >
                                <button
                                  id="ai-placement-component"
                                  type="button"
                                  onClick={() => setIsPickerOpen(true)}
                                  data-testid="ai-placement-component"
                                  className="field-control flex items-center justify-between gap-3 text-left"
                                >
                                  <span className="truncate">
                                    {selectedComponent
                                      ? `${selectedComponent.label} (${selectedComponent.id})`
                                      : 'Select a component'}
                                    {form.componentVersion ? (
                                      <span className="ml-2 text-gray-500 dark:text-gray-400">
                                        v{form.componentVersion}
                                      </span>
                                    ) : null}
                                  </span>
                                  <span className="shrink-0 text-xs text-gray-500 dark:text-gray-400">
                                    Browse
                                  </span>
                                </button>
                                {(check.errors.componentVersion ??
                                serverErrors.componentVersion) ? (
                                  <p className="field-error">
                                    {check.errors.componentVersion ?? serverErrors.componentVersion}
                                  </p>
                                ) : null}
                              </FormField>
                            </div>

                            <ModelSelect
                              id="ai-placement-model"
                              label="AI model"
                              testId="ai-placement-model"
                              value={form.model}
                              onChange={(value) => {
                                updateForm({ model: value });
                                check.touch('model');
                              }}
                              models={providers.data?.models}
                              error={check.errors.model ?? serverErrors.model}
                              helperText={
                                providers.error
                                  ? 'The model list could not be loaded. The saved model stays selected.'
                                  : 'Only models that are active in Settings › Models can be chosen.'
                              }
                            />

                            <div className="lg:col-span-2">
                              <FieldLabel>Status</FieldLabel>
                              <RadioCardGroup
                                name="ai-placement-status"
                                ariaLabel="Status"
                                value={form.status}
                                options={STATUS_OPTIONS}
                                onChange={(value) => updateForm({ status: value })}
                              />
                            </div>

                            <div className="lg:col-span-2">
                              <TextField
                                id="ai-placement-change-note"
                                label="Change note"
                                testId="ai-placement-change-note"
                                value={form.changeNote}
                                onChange={(value) => updateForm({ changeNote: value })}
                                onBlur={() => check.touch('changeNote')}
                                error={check.errors.changeNote ?? serverErrors.changeNote}
                                placeholder="What changed and why"
                                helperText="Recorded in the version history with this save."
                              />
                            </div>
                          </div>
                        </CardSection>

                        <CardSection
                          title="Prompt and instructions"
                          description="The template sent to the model, filled from the component's context."
                        >
                          <Textarea
                            id="ai-placement-prompt"
                            label="Prompt and instructions"
                            testId="ai-placement-prompt"
                            value={form.promptTemplate}
                            onChange={(value) => updateForm({ promptTemplate: value })}
                            onBlur={() => check.touch('promptTemplate')}
                            rows={10}
                            required
                            code
                            error={check.errors.promptTemplate ?? serverErrors.promptTemplate}
                            helperText="Write the instruction the model receives. Its output is rendered by the component."
                          />
                        </CardSection>
                      </CardBody>
                      <CardFooter>
                        <FormActions
                          form={FORM_ID}
                          onDiscard={() => {
                            reset();
                            check.reset();
                            save.clearError();
                            setServerErrors({});
                          }}
                          busy={save.busy}
                          disabled={!dirty}
                          disabledReason="No changes to save"
                          saveTestId="ai-placement-save"
                          discardTestId="ai-placement-discard"
                        />
                      </CardFooter>
                    </Card>
                  </form>
                </div>

                <div>
                  <Card>
                    <CardHeader
                      title="Preview and test"
                      description="Try the component against sample data before it goes live."
                    />
                    <CardBody>
                      <PlacementTestWorkspace
                        placementKey={form.placementKey}
                        componentId={form.componentId}
                        componentVersion={form.componentVersion}
                        sampleContext={sampleContext}
                        sampleProps={sampleProps}
                        hasUnsavedChanges={dirty}
                        onOpenTestData={(nextTab) => {
                          setTestDataTab(nextTab);
                          setIsTestDataOpen(true);
                        }}
                      />
                    </CardBody>
                  </Card>
                </div>
              </div>
            </section>
          ) : (
            <section
              role="tabpanel"
              id="placement-panel-history"
              aria-labelledby="placement-tab-history"
            >
              <Card>
                <CardHeader
                  title="Version history"
                  description="Every save, status change and restore, newest first."
                />
                <CardBody>
                  <PlacementHistoryTab
                    placementKey={form.placementKey}
                    placementName={title}
                    version={version}
                    onOpenVersion={(versionNumber) => viewer.open(String(versionNumber))}
                    onCloseVersion={viewer.close}
                    onRestored={(restored) => markSaved(form, formFromPlacement(restored))}
                  />
                </CardBody>
              </Card>
            </section>
          )}

          <ComponentPickerModal
            isOpen={isPickerOpen}
            onClose={() => setIsPickerOpen(false)}
            components={PLACEMENT_COMPONENTS}
            componentId={form.componentId}
            componentVersion={form.componentVersion}
            onSelect={(componentId, nextVersion) => {
              updateForm({ componentId, componentVersion: nextVersion.version });
              setSampleEdits({});
            }}
          />

          <TestDataModal
            isOpen={isTestDataOpen}
            onClose={() => setIsTestDataOpen(false)}
            initialTab={testDataTab}
            sampleContext={sampleContext}
            sampleProps={sampleProps}
            onContextChange={(context) => setSampleEdits((edits) => ({ ...edits, context }))}
            onPropsChange={(props) => setSampleEdits((edits) => ({ ...edits, props }))}
            componentVersion={selectedVersion}
            renderSnippet={renderSnippet}
          />

          <ConfirmModal
            isOpen={isDeleteOpen}
            title="Delete placement"
            message={`Delete ${placement ? displayNameOf(placement) : title}?`}
            confirmText={placement ? displayNameOf(placement) : title}
            confirmLabel="Delete placement"
            variant="danger"
            dangerNote="Its version history and test runs are deleted with it. This cannot be undone."
            testId="ai-placement-delete-confirm"
            onClose={() => setIsDeleteOpen(false)}
            onConfirm={() => void confirmDelete()}
          />
        </>
      )}
    </RecordPageShell>
  );
}
